<template>
  <CCard>
    <CCardHeader>
      <div class="card-header-actions">
        <CButton variant="outline" color="success" @click="onReset" ref="reset-button" v-translate
                 translate-context="form" class="mr-2">Reset
        </CButton>
        <CButton color="primary" @click="onSave" ref="save-button">{{ save_text }}</CButton>
      </div>
    </CCardHeader>
    <CCardBody class="p-0">
      <PTabs
        :tabs="tabs"
        :selected="activeTab"
        @select="activeTab = $event"
        pills
        alignLeft
        class="px-3 pt-3"
      />

      <CContainer v-show="activeTab === 'details'" fluid class="grid-stripped">
        <PFRow :label="$pgettext('base_plan.label', 'Name')">
          <PTranslatableTextInput v-model="form.name"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.label', 'Description')">
          <PTranslatableCkeditor v-model="form.description"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.label', 'Single Use')">
          <CSwitch class="mx-1" color="primary" variant="3d" size="sm" :checked.sync="form.single_use"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.label', 'Is Active')">
          <CSwitch class="mx-1" color="primary" variant="3d" size="sm" :checked.sync="form.is_active"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.label', 'Is Default')">
          <CSwitch class="mx-1" color="primary" variant="3d" size="sm" :checked.sync="form.is_default"/>
        </PFRow>
        <template v-if="category.is_subscription">
          <PFRow :label="$pgettext('base_plan.label', 'Monthly Price')">
            <CInput v-model="form.monthly_price"/>
          </PFRow>
          <PFRow :label="$pgettext('base_plan.label', 'Yearly Price')">
            <CInput v-model="form.yearly_price"/>
          </PFRow>
        </template>
        <template v-else>
          <PFRow :label="$pgettext('base_plan.label', 'Price')">
            <CInput v-model="form.price"/>
          </PFRow>
          <PFRow :label="$pgettext('base_plan.label', 'Recommended Participants')">
            <CInput v-model="form.recommended_participants"/>
          </PFRow>
        </template>
        <PFRow :label="$pgettext('base_plan.create.label','Tags')" size="12x12">
          <PTagInput v-model="form.tags" :allowed_tags="basePlanTags.options()"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.create.label','Template')" size="12x12">
          <PSelectKeyValue :options="searchTemplate" :value.sync="form.template" placeholder="Select Template"/>
        </PFRow>
        <PFRow :label="$pgettext('base_plan.create.label','Vertical')" size="12x12">
          <PSelectKeyValue :options="searchVertical" :value.sync="form.vertical" placeholder="Select Vertical"/>
        </PFRow>
        <template v-if="category.is_ecommerce">
          <PFRow :label="$pgettext('base_plan.label', 'Pica fee perc')"
                 :helpText="$pgettext('base_plan.help', 'Fee percentage for ecommerce sales. Enter the whole number (e.g. 12 for 12%, not 0.12). Leave 0 to use Contract Manager default.')">
            <CInput v-model="form.pica_fee_perc" type="number"/>
          </PFRow>
          <PFRow label="Assign transaction costs to pica"
                 :helpText="$pgettext('base_plan.help', 'Enable if the transaction costs are paid by Pica')">
            <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                     :checked.sync="form.assigns_transaction_costs_to_pica"/>
          </PFRow>
        </template>
      </CContainer>

      <CContainer v-show="activeTab === 'media'" fluid class="grid-stripped">
        <PFRow :label="$pgettext('plan.label','Included')">
          <CInput v-model="form.plan.num_photo_included" type="number"/>
        </PFRow>
        <PFRow :label="$pgettext('plan.label','Price for extra media')">
          <CInput v-model="form.plan.extra_photo_price" type="number"/>
        </PFRow>
        <PFRow :label="$pgettext('plan.label','Max uploadable media')">
          <CInput v-model="form.plan.num_max_photo" type="number"/>
        </PFRow>
        <PFRow :label="$pgettext('plan.label','Max participants (for plan choice)')">
          <CInput v-model="form.plan.max_participants" type="number"/>
        </PFRow>
      </CContainer>

      <div v-show="activeTab === 'features'" class="p-3">
        <component
          v-for="featureKey in basePlanFeatureKeys"
          :key="featureKey"
          :ref="'feature_' + featureKey"
          :is="basePlanFeatureComponents[featureKey]"
          :config="form.features ? form.features[featureKey] : null"
        />
      </div>
    </CCardBody>
  </CCard>

</template>

<script>
import PFRow from "@/domain/core/components/PFRow.vue";
import PTranslatableTextInput from "@/domain/core/components/PTranslatableTextInput.vue";
import PTranslatableCkeditor from "@/domain/core/components/PTranslatableCkeditor.vue";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import {mapActions, mapGetters} from "vuex";
import PTagInput from "@/domain/core/components/PTagInput.vue";
import basePlanTags from "@/domain/core/constant/basePlanTags";
import PTabs from "@/domain/core/components/PTabs.vue";
import FEATURE_COMPONENTS from "@/domain/picaServiceFeatures/features";

export default {
  name: "Form",
  computed: {
    ...mapGetters('region', [
      'category'
    ]),
    basePlanTags() {
      return basePlanTags
    },
    basePlanFeatureComponents() {
      return FEATURE_COMPONENTS
    },
    basePlanFeatureKeys() {
      return Object.keys(FEATURE_COMPONENTS)
    },
    tabs() {
      return [
        {name: 'details', label: this.$pgettext('base_plan.tabs', 'Details')},
        {name: 'media', label: this.$pgettext('base_plan.tabs', 'Media Limits')},
        {name: 'features', label: this.$pgettext('base_plan.tabs', 'Features')},
      ]
    },
  },
  components: {
    PTabs,
    PTagInput,
    PSelectKeyValue,
    PTranslatableCkeditor,
    PTranslatableTextInput,
    PFRow,
    ...FEATURE_COMPONENTS
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    save_text: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      activeTab: 'details',
      form: {
        name: {},
        price: '',
        yearly_price: '',
        monthly_price: '',
        recommended_participants: null,

        description: {},
        is_active: true,
        is_default: false,

        template: null,
        vertical: null,
        plan: {
          num_photo_included: null,
          extra_photo_price: null,
          num_max_photo: null,
          max_participants: null,
        },

        tags: [],
        single_use: false,
        pica_fee_perc: 0,
        assigns_transaction_costs_to_pica: false,
        features: {},
      },
    }
  },
  async mounted() {
    this.onReset()
    this.setRegionId(this.$route.params.region)
    await this.fetchCategory(this.$route.params.category)
  },
  emits: ['save'],
  methods: {
    ...mapActions('region', [
      'setRegionId',
      'fetchCategory',
      'searchVertical',
      'searchTemplate',
    ]),
    onReset() {
      this.form = {...this.data}
    },
    async onSave() {
      if (!this.form.price)
        this.form.price = 0
      if (!this.form.yearly_price)
        this.form.yearly_price = 0
      if (!this.form.monthly_price)
        this.form.monthly_price = 0
      this.form.is_subscription = this.category.is_subscription
      const features = {}
      for (const featureKey of this.basePlanFeatureKeys) {
        const ref = this.$refs['feature_' + featureKey]
        if (ref && ref[0] && typeof ref[0].getConfig === 'function') {
          features[featureKey] = ref[0].getConfig()
        }
      }
      this.form.features = features
      this.$emit('save', this.form)
    },
  }
}
</script>
